import { useState } from 'react';
import { Link } from 'react-router';
import Logo from '@/components/Logo';
import { useLogout } from '@/lib/session';
import { panierMemorise, useOrders } from '@/lib/orders';
import type { MeResponse } from '@/lib/api';
import { formatMoney } from '@/lib/utils';

/** Plan §5.2 — accueil : nouvelle commande, mes commandes du jour, mon total. */
export default function Home({ session }: { session: MeResponse }) {
  const logout = useLogout();
  // Limite haute : un show actif peut avoir beaucoup de brouillons partagés à
  // reprendre (tous les reps d'une même ligne les voient).
  const { data } = useOrders({ limit: 500 });
  const brouillonMemorise = panierMemorise();
  // Le montant vendu est masqué par défaut : un rep ne montre pas son total au
  // client par-dessus l'épaule. L'œil le révèle au besoin.
  const [montantVisible, setMontantVisible] = useState(false);
  // Listes repliables, FERMÉES par défaut à l'ouverture du compte : elles peuvent
  // être longues (brouillons et commandes soumises partagés entre reps d'une ligne).
  const [brouillonsOuverts, setBrouillonsOuverts] = useState(false);
  const [soumisesOuvertes, setSoumisesOuvertes] = useState(false);

  // On masque les commandes annulées de l'accueil du rep : elles ne lui sont plus
  // utiles (l'admin les gère depuis son onglet Commandes).
  const commandes = (data?.orders ?? []).filter((o) => o.status !== 'CANCELLED');
  const soumises = commandes.filter((o) => o.status === 'SUBMITTED');
  // TOUS les brouillons en cours (pas seulement le dernier), celui qu'on éditait
  // (mémorisé en localStorage) remonté en tête pour un accès immédiat.
  const brouillons = commandes
    .filter((o) => o.status === 'DRAFT')
    .sort((a, b) => (a.id === brouillonMemorise ? -1 : b.id === brouillonMemorise ? 1 : 0));

  return (
    <main className="mx-auto max-w-4xl space-y-6 p-6">
      {/* En-tête sur l'affiche officielle Super Show. Logo, infos et déconnexion
          sont posés sur des surfaces blanches pour rester lisibles par-dessus. */}
      <header
        className="overflow-hidden rounded-2xl bg-cover bg-center p-5 shadow-sm"
        style={{ backgroundImage: 'url(/bg-super-show.jpg)' }}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="bg-card rounded-xl px-4 py-3 shadow-sm">
            <Logo className="mb-2 h-10" />
            <h1 className="text-2xl leading-tight font-bold">{session.user.fullName}</h1>
            <p className="text-muted-foreground text-sm">
              {session.user.username} ·{' '}
              {session.user.role === 'ADMIN'
                ? 'Administrateur'
                : session.user.role === 'REP_SUPER'
                  ? 'Représentant Super'
                  : session.user.role === 'REP_JEUX'
                    ? 'Compte Jeux'
                    : 'Représentant'}
            </p>
          </div>
          <button
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
            className="bg-card text-foreground h-12 shrink-0 rounded-lg px-5 font-medium shadow-sm disabled:opacity-50"
          >
            Déconnexion
          </button>
        </div>
      </header>

      {/* Commandes non terminées : le rep choisit LAQUELLE reprendre (pas seulement
          la dernière). Un iPad qui se verrouille ou une PWA déchargée par Safari ne
          doit pas faire perdre une commande en cours. */}
      {brouillons.length > 0 && (
        <section className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
          <button
            type="button"
            onClick={() => setBrouillonsOuverts((v) => !v)}
            aria-expanded={brouillonsOuverts}
            className="flex w-full items-center justify-between gap-3 text-left"
          >
            <div>
              <h2 className="font-semibold">
                Commandes en cours à reprendre ({brouillons.length})
              </h2>
              <p className="text-muted-foreground text-xs">
                Y compris les brouillons de vos collègues de la même ligne.
              </p>
            </div>
            <span aria-hidden className="text-muted-foreground shrink-0 text-xl">
              {brouillonsOuverts ? '▾' : '▸'}
            </span>
          </button>

          {brouillonsOuverts && (
            <ul className="mt-2 max-h-96 divide-y divide-amber-500/25 overflow-y-auto">
              {brouillons.map((o) => (
                <li key={o.id}>
                  <Link
                    to={`/order/${o.id}`}
                    className="flex items-center justify-between gap-3 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="font-medium">{o.customerName}</p>
                      <p className="text-muted-foreground font-mono text-xs">
                        {o.custNum}
                        {o.id === brouillonMemorise && ' · en cours'}
                      </p>
                    </div>
                    <p className="shrink-0 font-semibold tabular-nums">
                      {montantVisible ? formatMoney(o.total) : '••••'}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <div className="flex flex-wrap gap-3">
        <Link
          to="/order/new"
          className="bg-primary text-primary-foreground inline-flex h-20 flex-1 items-center justify-center rounded-xl px-8 text-xl font-semibold"
        >
          Nouvelle commande
        </Link>
        {(session.user.role === 'REP_SUPER' || session.user.role === 'REP_JEUX') && (
          <Link
            to="/consultation"
            className="border-input inline-flex h-20 items-center rounded-xl border px-8 text-lg font-semibold"
          >
            Consultation client
          </Link>
        )}
        {session.user.role === 'ADMIN' && (
          <Link
            to="/admin"
            className="border-input inline-flex h-20 items-center rounded-xl border px-8 text-lg font-semibold"
          >
            Administration
          </Link>
        )}
      </div>

      <section className="bg-card rounded-xl border p-6">
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => setSoumisesOuvertes((v) => !v)}
            aria-expanded={soumisesOuvertes}
            className="flex min-w-0 items-center gap-2 text-left"
          >
            <span aria-hidden className="text-muted-foreground text-xl">
              {soumisesOuvertes ? '▾' : '▸'}
            </span>
            <h2 className="font-semibold">Mes commandes soumises ({soumises.length})</h2>
          </button>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold tabular-nums">
              {montantVisible ? formatMoney(data?.submittedTotal ?? '0') : '••••••'}
            </p>
            <button
              type="button"
              onClick={() => setMontantVisible((v) => !v)}
              aria-label={montantVisible ? 'Masquer le montant' : 'Afficher le montant'}
              className="text-muted-foreground hover:text-foreground rounded-lg p-2"
            >
              <EyeIcon open={montantVisible} />
            </button>
          </div>
        </div>

        {soumisesOuvertes &&
          (soumises.length === 0 ? (
            <p className="text-muted-foreground mt-4 text-sm">
              Aucune commande soumise pour l'instant.
            </p>
          ) : (
            <ul className="mt-4 max-h-96 divide-y overflow-y-auto">
              {soumises.map((o) => (
                <li key={o.id}>
                  <Link to={`/order/${o.id}`} className="flex items-center justify-between gap-3 py-3">
                    <div className="min-w-0">
                      <p className="font-medium">{o.customerName}</p>
                      <p className="text-muted-foreground font-mono text-xs">
                        {o.number ?? '—'} · {o.custNum}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="font-semibold tabular-nums">
                        {montantVisible ? formatMoney(o.total) : '••••'}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          ))}
      </section>
    </main>
  );
}

/** Œil ouvert / barré pour révéler ou masquer le montant. */
function EyeIcon({ open }: { open: boolean }) {
  const common = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  return open ? (
    <svg {...common} aria-hidden>
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  ) : (
    <svg {...common} aria-hidden>
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 5c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.5 13.5 0 0 0 2 12s3.5 7 10 7a9.7 9.7 0 0 0 5.39-1.61" />
      <line x1="2" y1="2" x2="22" y2="22" />
    </svg>
  );
}
